"use client";

import { useCallback, useRef, useSyncExternalStore } from "react";

interface PersistedData<T> {
  data: T;
  timestamp: number;
}

interface UseFormPersistenceOptions {
  /** Time to live in milliseconds. Default: 24 hours */
  ttl?: number;
  /** Debounce delay for saves in milliseconds. Default: 500ms */
  debounceMs?: number;
}

export const DEFAULT_TTL = 24 * 60 * 60 * 1000; // 24 hours
const DEFAULT_DEBOUNCE = 500; // 500ms

/**
 * Hook for persisting form data to localStorage with auto-save and TTL.
 *
 * Features:
 * - Auto-saves form data on change (debounced)
 * - Auto-restores data on mount if within TTL (SSR-safe)
 * - Returns wasRestored flag to know if data was restored
 * - Provides clear function for successful submission
 *
 * @example
 * ```tsx
 * const [formData, setFormData, { wasRestored, clearSavedData }] =
 *   useFormPersistence("quick_request_form", initialFormData);
 *
 * // wasRestored is true if localStorage had valid data that was restored
 * // On successful submission:
 * clearSavedData();
 * ```
 */
export function useFormPersistence<T>(
  key: string,
  initialData: T,
  options?: UseFormPersistenceOptions
): [
  T,
  (data: T | ((prev: T) => T)) => void,
  {
    wasRestored: boolean;
    /**
     * `wasRestored` as it is known during RENDER, which on a hydration render
     * is still the server snapshot's `false` — React serves `getServerSnapshot()`
     * for that pass and only swaps the real store in on the following render
     * (the same lag `components/ui/PhoneInput.tsx` documents for `data`).
     *
     * Anything that reads the flag from an EFFECT must read this ref instead:
     * the eager init below runs in the component body, so `.current` is already
     * correct when the first passive effect fires. `useFunnelAnalytics` emits
     * `was_restored` on a mount effect and latches a `flowStarted` ref, so
     * reading the render value there produced `false` for 100 % of genuinely
     * restored sessions — a hard page load is exactly how a restore arrives.
     */
    wasRestoredRef: React.RefObject<boolean>;
    /**
     * The form data as it is known at EFFECT time, for the same reason as
     * `wasRestoredRef`: a hydration render serves `initialData`, so anything
     * emitted from a mount effect that describes the form would describe the
     * EMPTY one. Do not render from this — `data` above is what keeps the
     * server and client markup identical.
     */
    dataRef: React.RefObject<T>;
    clearSavedData: () => void;
    resetForm: () => void;
    flushToStorage: () => void;
  },
] {
  const ttl = options?.ttl ?? DEFAULT_TTL;
  const debounceMs = options?.debounceMs ?? DEFAULT_DEBOUNCE;

  // Store for the current state - this is the source of truth
  const storeRef = useRef<{ data: T; wasRestored: boolean; version: number }>({
    data: initialData,
    wasRestored: false,
    version: 0,
  });

  // Stable server snapshot (must be cached)
  const serverSnapshotRef = useRef<{ data: T; wasRestored: boolean; version: number }>({
    data: initialData,
    wasRestored: false,
    version: 0,
  });

  // Effect-time truth for `wasRestored` — see the return type. Written by the
  // eager init below, which runs during render, so it is correct before any
  // effect in the tree runs.
  const wasRestoredRef = useRef(false);
  const dataRef = useRef<T>(initialData);

  // Track initialization state
  const initializedRef = useRef(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Subscribers for triggering re-renders
  const subscribersRef = useRef<Set<() => void>>(new Set());

  const subscribe = useCallback((callback: () => void) => {
    subscribersRef.current.add(callback);
    return () => subscribersRef.current.delete(callback);
  }, []);

  const notifySubscribers = useCallback(() => {
    storeRef.current.version += 1;
    subscribersRef.current.forEach((cb) => cb());
  }, []);

  // Initialize from localStorage eagerly (before useSyncExternalStore).
  // This keeps getSnapshot pure — no side effects during concurrent reads.
  if (!initializedRef.current && typeof window !== "undefined") {
    initializedRef.current = true;
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed: PersistedData<T> = JSON.parse(stored);
        // eslint-disable-next-line react-hooks/purity -- Intentional eager init before useSyncExternalStore
        const age = Date.now() - parsed.timestamp;
        if (age < ttl) {
          storeRef.current = {
            // Merge with initialData so new fields added after the data was
            // persisted still get their defaults (e.g. unknownFields: [])
            data: { ...initialData, ...parsed.data },
            wasRestored: true,
            version: 1,
          };
          wasRestoredRef.current = true;
          dataRef.current = storeRef.current.data;
        } else {
          localStorage.removeItem(key);
          storeRef.current.version = 1;
        }
      } else {
        storeRef.current.version = 1;
      }
    } catch {
      localStorage.removeItem(key);
      storeRef.current.version = 1;
    }
  }

  // Snapshot must be pure — just returns the store ref
  const getSnapshot = useCallback(() => {
    return storeRef.current;
  }, []);

  const getServerSnapshot = useCallback(() => {
    // Return the cached server snapshot ref
    return serverSnapshotRef.current;
  }, []);

  // Use useSyncExternalStore to manage the data
  const store = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Save to localStorage (debounced)
  const saveToStorage = useCallback(
    (newData: T) => {
      if (typeof window === "undefined") return;

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        try {
          const persisted: PersistedData<T> = {
            data: newData,
            timestamp: Date.now(),
          };
          localStorage.setItem(key, JSON.stringify(persisted));
        } catch {
          console.warn("Failed to save form data to localStorage");
        }
      }, debounceMs);
    },
    [key, debounceMs]
  );

  // Setter function
  const setData = useCallback(
    (dataOrUpdater: T | ((prev: T) => T)) => {
      const currentData = storeRef.current.data;
      const newData =
        typeof dataOrUpdater === "function"
          ? (dataOrUpdater as (prev: T) => T)(currentData)
          : dataOrUpdater;

      storeRef.current = {
        ...storeRef.current,
        data: newData,
      };
      dataRef.current = newData;
      saveToStorage(newData);
      notifySubscribers();
    },
    [saveToStorage, notifySubscribers]
  );

  // Clear saved data
  const clearSavedData = useCallback(() => {
    if (typeof window === "undefined") return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    localStorage.removeItem(key);
  }, [key]);

  // Reset form to initial state and clear storage
  const resetForm = useCallback(() => {
    clearSavedData();
    storeRef.current = {
      data: initialData,
      wasRestored: false,
      version: storeRef.current.version + 1,
    };
    wasRestoredRef.current = false;
    dataRef.current = initialData;
    notifySubscribers();
  }, [clearSavedData, initialData, notifySubscribers]);

  // Force-write current data to localStorage immediately (bypasses debounce)
  const flushToStorage = useCallback(() => {
    if (typeof window === "undefined") return;
    try {
      const persisted: PersistedData<T> = {
        data: storeRef.current.data,
        timestamp: Date.now(),
      };
      localStorage.setItem(key, JSON.stringify(persisted));
    } catch {
      // ignore
    }
  }, [key]);

  return [
    store.data,
    setData,
    {
      wasRestored: store.wasRestored,
      wasRestoredRef,
      dataRef,
      clearSavedData,
      resetForm,
      flushToStorage,
    },
  ];
}
